import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Storage } from "@ionic/storage";
import { BehaviorSubject, Observable } from "rxjs";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Platform } from "ionic-angular";
import { ILogin } from "../../model/top.model";
import { LOGIN, OS_TYPE } from "../../enums/enums";
import { ENV } from '@app/env';

@Injectable()
export class AuthService {
  appVersion = ENV.appversion + "_" + ENV.version;
  isAuthenticated: boolean;
  private _isAuthenticated: BehaviorSubject<boolean>;
  apiUrl = ENV.apiUrl;

  get isAuthenticated$BS() {
    return this._isAuthenticated;
  }

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private platform: Platform,
    private jwtHelper: JwtHelperService
  ) {
    this._isAuthenticated = new BehaviorSubject(false);
    this.isAuthenticated$BS.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });
  }

  checkIsAuthenticated(): Promise<boolean> {
    return new Promise((resolve) => {
      this.storage.get(LOGIN.LOGIN_OBJECT).then((login: ILogin) => {
        // Check whether the token is valid and not expired and return true or false
        if (!login) {
          this._isAuthenticated.next(false);
          resolve(false);
          return;
        }
        try {
          this.jwtHelper.decodeToken(login.token);
        } catch (error) {
          console.error(error);
          this.storage.remove(LOGIN.LOGIN_OBJECT);
          this._isAuthenticated.next(false);
          resolve(false);
          return;
        }
        console.log(
          "checkIsAuthenticated token expired: " +
          this.jwtHelper.isTokenExpired(login.token)
        );
        if (this.jwtHelper.isTokenExpired(login.token)) {
          try {
            this.jwtHelper.decodeToken(login.refresh_token);
          } catch (error) {
            console.error(error);
            this.storage.remove(LOGIN.LOGIN_OBJECT);
            this._isAuthenticated.next(false);
            resolve(false);
            return;
          }
          console.log(
            "checkIsAuthenticated refresh_token expired: " +
            this.jwtHelper.isTokenExpired(login.refresh_token)
          );
          if (this.jwtHelper.isTokenExpired(login.refresh_token)) {
            this._isAuthenticated.next(false);
            resolve(false);
            return;
          }
          this.login(null, null, login.refresh_token).subscribe(
            (loginResult) => {
              this.storage.set(LOGIN.LOGIN_OBJECT, loginResult);
              this._isAuthenticated.next(true);
              resolve(true);
              return;
            },
            (error) => {
              console.error(error);
              this.storage.remove(LOGIN.LOGIN_OBJECT);
              this._isAuthenticated.next(false);
              resolve(false);
              return;
            }
          );
        } else {
          this._isAuthenticated.next(true);
          resolve(true);
          return;
        }
      });
    });
  }

  getAuthorizationToken(): Promise<string> {
    return new Promise((resolve) => {
      this.storage.get(LOGIN.LOGIN_OBJECT).then((login) => {
        login ? resolve(login.token) : resolve(null);
      });
    });
  }

  getAuthorizationLogin(): Promise<ILogin> {
    return this.storage.get(LOGIN.LOGIN_OBJECT);
  }

  setAuthorizationLogin(login: ILogin): Promise<any> {
    return this.storage.set(LOGIN.LOGIN_OBJECT, login);
  }

  removeAuthorizationLogin(): Promise<any> {
    return this.storage.remove(LOGIN.LOGIN_OBJECT);
  }

  login(
    username: string,
    password: string,
    refreshToken: string
  ): Observable<ILogin> {
    const type = refreshToken ? 'REFRESH' : 'LOGIN';
    const json = {
      username,
      password,
      refreshToken,
      type,
      appversion: this.appVersion,
      ostype: this.platform.is("ios")
        ? OS_TYPE.IOS
        : this.platform.is("android")
          ? OS_TYPE.ANDROID
          : OS_TYPE.WEB,
    };
    return this.http.post<ILogin>(`${this.apiUrl}login/`, JSON.stringify(json));
  }

  logout(): Promise<void> {
    return new Promise((resolve) => {
      this.removeAuthorizationLogin().then(() => {
        this._isAuthenticated.next(false);
        resolve();
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth/auth.service.ts